import React, { useState, useEffect } from 'react';
import { LocationInput } from './LocationInput';
import { LocationData } from '../types';
import { healthAPI } from '../api';
import { useUser } from '../contexts/UserContext'; // Import useUser

import './QuickEntry.css'; // Reusing for form styles

interface WaterFormProps {
    onSuccess?: (traceId: string) => void;
    onError?: (error: string) => void;
}

export const WaterForm: React.FC<WaterFormProps> = ({ onSuccess, onError }) => {
    const { profile } = useUser(); // Get profile from context

    // Initialize defaults from profile if available, otherwise defaults
    const [amount, setAmount] = useState<string>('');
    // Use profile unit or default to ml. This prevents flashing if profile is already loaded.
    const [unit, setUnit] = useState<'ml' | 'oz'>(profile?.settings?.units?.water || 'ml');
    const [container] = useState<string>('bottle');
    const [context, setContext] = useState<string>('');
    const [location, setLocation] = useState<LocationData | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Progress state
    const [todayTotal, setTodayTotal] = useState<number>(0);
    const [goal, setGoal] = useState<number>(profile?.settings?.daily_water_goal_ml || 2000);


    useEffect(() => {
        // Update local state if profile changes (e.g. initial load finishes)
        if (profile?.settings?.units?.water) {
            setUnit(profile.settings.units.water);
        }
        if (profile?.settings?.daily_water_goal_ml) {
            setGoal(profile.settings.daily_water_goal_ml);
        }
    }, [profile]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            // Profile is handled by context now, just load events

            // Load today's water events to calculate total

            // Load today's water events to calculate total
            // Load today's water events to calculate total
            // Use local time start of day
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);

            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);

            const start = startOfDay.toISOString();
            const end = endOfDay.toISOString();

            const events = await healthAPI.getEvents({
                start,
                end,
                type: 'water' // Note: This assumes getEvents supports filtering by 'water' now, which the backend does
            });

            let total = 0;
            events.events.forEach(e => {
                if (e.type === 'water' && 'amount_ml' in e.metadata) {
                    // @ts-ignore
                    total += e.metadata.amount_ml || 0;
                }
            });
            setTodayTotal(total);

        } catch (err) {
            console.error('Failed to load water data', err);
        } finally {

        }
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        if (!amount) return;

        setIsSubmitting(true);
        try {
            const amountNum = parseFloat(amount);

            // Calculate ml based on unit
            let amountMl = amountNum;

            if (unit === 'oz') {

                // Backend handles conversion or we send it? 
                // Plan said: "If 'oz', convert to ml for backend storage" or "Send both"
                // Backend code I wrote accepts amount_oz and converts.
                // So I can just send what I have.
            } else {
                amountMl = amountNum;
            }

            const eventData: any = {
                amount_ml: unit === 'ml' ? amountMl : (amountNum * 29.5735),
                amount_oz: unit === 'oz' ? amountNum : undefined,
                container,
                notes: context  // Add context as notes
            };

            if (location) {
                eventData.location = location;
            }

            await healthAPI.createEvent({
                type: 'water',
                data: eventData
            });

            setAmount('');
            setContext('');
            loadData(); // Reload progress
            if (onSuccess) onSuccess('logged');

        } catch (error) {
            console.error(error);
            if (onError) onError('Failed to log water');
        } finally {
            setIsSubmitting(false);
        }
    };

    const quickAdd = (amountVal: number) => {
        setAmount(amountVal.toString());
        // Small hack: wait for state update? or just call submit directly with value
        // Better to extract submit logic.
        submitValue(amountVal);
    };

    const submitValue = async (val: number) => {
        setIsSubmitting(true);
        try {
            const amountMl = unit === 'ml' ? val : (val * 29.5735);
            await healthAPI.createEvent({
                type: 'water',
                data: {
                    amount_ml: amountMl,
                    amount_oz: unit === 'oz' ? val : undefined,
                    container: 'quick-add'
                }
            });
            loadData();
            if (onSuccess) onSuccess('logged');
        } catch (error) {
            if (onError) onError('Failed to log water');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Visualization helpers
    const displayTotal = unit === 'ml' ? Math.round(todayTotal) : Math.round(todayTotal / 29.5735);
    const displayGoal = unit === 'ml' ? goal : Math.round(goal / 29.5735);
    const percent = Math.min(100, Math.round((todayTotal / goal) * 100));

    return (
        <div className="water-tracker">
            <div className={`water-progress-card ${percent >= 100 ? 'goal-met' : ''}`}>
                <div className="progress-circle" style={{ background: `conic-gradient(#4facfe ${percent}%, #e0eafc 0)` }}>
                    <div className="inner-circle">
                        <span className="percent">{percent}%</span>
                        <span className="fraction">{displayTotal} / {displayGoal} {unit}</span>
                    </div>
                </div>
                <h3>hydration level</h3>
            </div>

            <div className="quick-actions">
                <button onClick={() => quickAdd(unit === 'ml' ? 250 : 8)}>
                    + {unit === 'ml' ? '250ml' : '8oz'}
                </button>
                <button onClick={() => quickAdd(unit === 'ml' ? 500 : 16)}>
                    + {unit === 'ml' ? '500ml' : '16oz'}
                </button>
            </div>

            <form onSubmit={handleSubmit} className="custom-entry">
                <div className="input-group">
                    <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Amount"
                    />
                    <select value={unit} onChange={(e) => setUnit(e.target.value as any)}>
                        <option value="ml">ml</option>
                        <option value="oz">oz</option>
                    </select>
                </div>

                <div className="input-group">
                    <input
                        type="text"
                        value={context}
                        onChange={(e) => setContext(e.target.value)}
                        placeholder="Context (e.g. 'gym')"
                        style={{ width: '100%' }}
                    />
                </div>

                <LocationInput onLocationChange={setLocation} />

                <button type="submit" disabled={isSubmitting || !amount}>Log</button>
            </form>

            <style>{`
                .water-tracker {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    max-width: 400px;
                    margin: 0 auto;
                }
                .water-progress-card {
                    background: white;
                    padding: 20px;
                    border-radius: 16px;
                    text-align: center;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }
                .progress-circle {
                    width: 150px;
                    height: 150px;
                    margin: 0 auto 15px;
                    border-radius: 50%;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .inner-circle {
                    width: 130px;
                    height: 130px;
                    background: white;
                    border-radius: 50%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }
                .percent {
                    font-size: 2em;
                    font-weight: bold;
                    color: #4facfe;
                }
                .fraction {
                    color: #888;
                    font-size: 0.9em;
                }
                .quick-actions {
                    display: flex;
                    gap: 10px;
                }
                .quick-actions button {
                    flex: 1;
                    padding: 15px;
                    border: none;
                    background: white;
                    border-radius: 12px;
                    font-weight: 600;
                    color: #4facfe;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                    cursor: pointer;
                    transition: transform 0.1s;
                }
                .quick-actions button:active {
                    transform: scale(0.98);
                }
                .custom-entry {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .input-group {
                    display: flex;
                    gap: 10px;
                }
                .input-group input {
                    flex: 1;
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                }
                .custom-entry button {
                    padding: 12px;
                    background: #4facfe;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                }
            `}</style>
        </div>
    );
};
